import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useTenantThemeApi from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantThemeApi';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import getNavConfig from './NavConfig';
import CollapseButton from './CollapseButton';
import { SHOW_SIDEBAR_COMPONENT, useEventBus } from 'src/event-bus';
import { checkPermissions } from 'src/careflow/ph-core/admin/permissions/util/get.user.permissions';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';

// ----------------------------------------------------------------------

const EnvironmentLabel = styled('div')<{ bgColor?: string }>(({ theme, bgColor = '#FB8C00' }) => ({
  position: 'absolute',
  left: -28,
  top: 12,
  width: 113,
  height: 32,
  backgroundColor: bgColor,
  color: 'white',
  fontFamily: '"Public Sans", sans-serif',
  fontWeight: 700,
  fontSize: 12,
  lineHeight: '18px',
  letterSpacing: '1.1px',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'rotate(-45deg)',
  boxShadow: '0px 1px 1px rgba(145, 158, 171, 0.8)',
  zIndex: 1100,
}));

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: Props) {
  const theme = useTheme();

  const { pathname } = useLocation();

  const auth = useAuth();

  const isDesktop = useResponsive('up', 'lg');

  const { environment, bgColor } = useTenantThemeApi();

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    checkPermissions();
    auth.checkTenantCodeInMemoryNToken();
  });

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const { onEvent, removeEvent } = useEventBus();
  const [sideComponent, setSideComponent] = useState<JSX.Element | null>(null);

  const callback = (data: any) => setSideComponent(data);

  useEffect(() => {
    onEvent(SHOW_SIDEBAR_COMPONENT, callback);
    return () => removeEvent(SHOW_SIDEBAR_COMPONENT, callback);
  }, []);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          position: 'relative',
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        {environment && (
          <EnvironmentLabel bgColor={bgColor || '#FB8C00'}>
            {environment.toUpperCase()}
          </EnvironmentLabel>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo />

          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>
      </Stack>

      {sideComponent ?? <NavSectionVertical navConfig={getNavConfig()} isCollapse={isCollapse} />}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
